// The dashboard Team view (round 3 §2): users with role pills, invite by
// email, change role, remove, transfer ownership with typed confirmation,
// pending invites with resend. Admin+ (the server enforces it; this renders
// the staff explanation rather than a broken surface). Built against the
// existing token set; Design's /signin + Team frames re-skin later.
import { useCallback, useEffect, useState } from 'react';
import { authApi } from '@/lib/auth.js';
import Modal from '@/components/ui/Modal.jsx';

const ROLE_HELP = {
  owner: 'Everything, including ownership transfer',
  admin: 'Everything except ownership transfer and billing-export settings',
  staff: 'Roster read, log outside COIs, invite students; no requirements edits or user management',
};

function RolePill({ role }) {
  const tone = role === 'owner' ? 'bg-primary text-white' : role === 'admin' ? 'bg-teal-fill text-primary' : 'bg-fill text-ink-dim';
  return <span title={ROLE_HELP[role]} className={`rounded-pill px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.04em] ${tone}`}>{role}</span>;
}

export default function Team({ slug, me }) {
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [transferConfirm, setTransferConfirm] = useState('');

  const load = useCallback(() => {
    authApi.get(`/api/ctr/schools/${encodeURIComponent(slug)}/team`)
      .then((r) => { setTeam(r); setError(null); })
      .catch((e) => setError(e.code === 'role_insufficient' ? 'staff' : 'load'));
  }, [slug]);
  useEffect(load, [load]);

  async function act(fn, okNotice) {
    setBusy(true); setNotice(null);
    try { await fn(); setNotice(okNotice); load(); }
    catch (e) { setNotice(`That didn't take: ${e.code || e.message}`); }
    finally { setBusy(false); }
  }

  if (error === 'staff') {
    return (
      <div className="max-w-[560px] rounded-panel border border-line bg-white p-[26px_28px] text-[13.5px] leading-[1.6] text-ink-dim">
        <b className="text-ink">Team management needs an admin.</b> Your role is staff: you can work the roster,
        log outside COIs, and invite students, but user management belongs to your school&apos;s admins and owner.
      </div>
    );
  }
  if (error) return <div className="p-4 text-[13px] text-warn-fg">Couldn&apos;t load the team just now. Refresh to retry.</div>;
  if (!team) return <div className="p-4 text-[13px] text-ink-dim">Loading…</div>;

  const isOwner = team.your_role === 'owner';
  const members = team.users || [];

  return (
    <div className="max-w-[760px]">
      {notice ? <div className="mb-3.5 rounded-card bg-teal-faint p-[10px_14px] text-[12.5px] text-teal-mute">{notice}</div> : null}

      <div className="mb-4 overflow-hidden rounded-panel border border-line bg-white">
        <div className="bg-fill p-[12px_18px] text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-dim">People</div>
        {members.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-fill p-[13px_18px]">
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold">{u.name || u.email}{u.email === me ? <span className="ml-2 text-[11px] font-normal text-ink-faint">(you)</span> : null}</div>
              {u.name ? <div className="text-[11.5px] text-ink-faint">{u.email}</div> : null}
            </div>
            <div className="flex items-center gap-2.5">
              <RolePill role={u.role} />
              {u.role !== 'owner' ? (
                <>
                  <select
                    aria-label={`Role for ${u.email}`}
                    value={u.role} disabled={busy}
                    onChange={(e) => act(
                      () => authApi.patch(`/api/ctr/schools/${encodeURIComponent(slug)}/team/${u.id}`, { role: e.target.value }),
                      `${u.email} is now ${e.target.value}.`,
                    )}
                    className="rounded-lg border-[1.5px] border-line bg-white p-[7px_9px] text-[12.5px] font-semibold outline-none">
                    <option value="admin">admin</option>
                    <option value="staff">staff</option>
                  </select>
                  <button type="button" disabled={busy}
                    onClick={() => act(
                      () => authApi.del(`/api/ctr/schools/${encodeURIComponent(slug)}/team/${u.id}`),
                      `${u.email} removed.`,
                    )}
                    className="min-h-[36px] rounded-lg border border-line px-3 text-[12px] font-semibold text-ink-dim hover:border-bad-fg hover:text-bad-fg">
                    Remove
                  </button>
                </>
              ) : isOwner ? (
                <button type="button" onClick={() => setTransferOpen(true)}
                  className="min-h-[36px] rounded-lg border border-line px-3 text-[12px] font-semibold text-ink-dim hover:border-primary hover:text-primary">
                  Transfer ownership
                </button>
              ) : null}
            </div>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2.5 border-t border-fill bg-paper p-[13px_18px]">
          <input
            type="email" placeholder="teammate@yourschool.com" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="min-h-[42px] w-[240px] rounded-[9px] border-[1.5px] border-line p-[9px_12px] text-[13px] outline-none focus:border-accent" />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} aria-label="Invite role"
            className="min-h-[42px] rounded-[9px] border-[1.5px] border-line bg-white p-[9px_10px] text-[13px] font-semibold outline-none">
            <option value="staff">staff</option>
            <option value="admin">admin</option>
          </select>
          <button type="button" disabled={busy || !inviteEmail}
            onClick={() => act(
              () => authApi.post(`/api/ctr/schools/${encodeURIComponent(slug)}/team/invite`, { email: inviteEmail, role: inviteRole }).then(() => setInviteEmail('')),
              `Invite sent to ${inviteEmail}. It expires in 7 days.`,
            )}
            className="min-h-[42px] rounded-[9px] bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary-deep disabled:opacity-60">
            Invite by email
          </button>
          <span className="text-[11.5px] text-ink-faint">One email with a sign-in link; they land on this dashboard.</span>
        </div>
      </div>

      {(team.invites || []).length ? (
        <div className="overflow-hidden rounded-panel border border-line bg-white">
          <div className="bg-fill p-[12px_18px] text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-dim">Pending invites</div>
          {team.invites.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-fill p-[13px_18px]">
              <div>
                <div className="text-[13.5px] font-semibold">{i.email}</div>
                <div className="text-[11.5px] text-ink-faint">
                  invited as {i.role} by {i.invited_by || 'a teammate'} · {i.expired ? 'expired' : `expires ${new Date(i.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {i.expired ? <span className="rounded-pill bg-warn-bg px-2 py-[3px] text-[10px] font-bold uppercase text-warn-fg">expired</span> : null}
                <button type="button" disabled={busy}
                  onClick={() => act(
                    () => authApi.post(`/api/ctr/schools/${encodeURIComponent(slug)}/team/invite/${i.id}/resend`, {}),
                    `Invite re-sent to ${i.email}; the 7-day clock restarted.`,
                  )}
                  className="min-h-[36px] rounded-lg border border-line px-3 text-[12px] font-semibold text-ink-dim hover:border-primary hover:text-primary">
                  Re-send
                </button>
                <button type="button" disabled={busy}
                  onClick={() => act(
                    () => authApi.del(`/api/ctr/schools/${encodeURIComponent(slug)}/team/invite/${i.id}`),
                    `Invite to ${i.email} revoked.`,
                  )}
                  className="min-h-[36px] rounded-lg border border-line px-3 text-[12px] font-semibold text-ink-dim hover:border-bad-fg hover:text-bad-fg">
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <Modal open={transferOpen} onClose={() => { setTransferOpen(false); setTransferConfirm(''); setTransferTo(''); }} maxWidth="max-w-[480px]">
        <h2 className="mb-1.5 text-[19px] font-bold tracking-[-0.01em]">Transfer ownership</h2>
        <p className="mb-4 text-[13px] leading-[1.6] text-ink-dim">
          The new owner gets everything, including the ability to transfer again. You become an admin.
          This is the one action only the owner can take.
        </p>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.04em] text-ink-soft">New owner</label>
        <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)}
          className="mb-3.5 min-h-[44px] w-full rounded-[9px] border-[1.5px] border-line bg-white p-[10px_12px] text-[13.5px] outline-none">
          <option value="">Pick a team member…</option>
          {members.filter((u) => u.role !== 'owner').map((u) => (
            <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>
          ))}
        </select>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.04em] text-ink-soft">Type <span className="font-mono normal-case">TRANSFER {slug}</span> to confirm</label>
        <input value={transferConfirm} onChange={(e) => setTransferConfirm(e.target.value)}
          placeholder={`TRANSFER ${slug}`}
          className="mb-4 min-h-[44px] w-full rounded-[9px] border-[1.5px] border-line p-[10px_12px] font-mono text-[13.5px] outline-none focus:border-accent" />
        <button type="button" disabled={busy || !transferTo || transferConfirm !== `TRANSFER ${slug}`}
          onClick={() => act(
            () => authApi.post(`/api/ctr/schools/${encodeURIComponent(slug)}/transfer-ownership`, { to_user_id: transferTo, confirm: transferConfirm })
              .then(() => { setTransferOpen(false); setTransferConfirm(''); setTransferTo(''); }),
            'Ownership transferred. You are now an admin.',
          )}
          className="min-h-[48px] w-full rounded-card-lg bg-primary text-[14px] font-bold text-white hover:bg-primary-deep disabled:cursor-not-allowed disabled:opacity-50">
          Transfer ownership
        </button>
      </Modal>
    </div>
  );
}
